import { ActionReducerMapBuilder, Draft, PayloadAction } from '@reduxjs/toolkit'
import {
  DataList,
  saveErrorInState,
  dismissError as commonDismissError,
  closeAlert as commonCloseAlert,
  setAlert as commonSetAlert,
  setAlertState,
  setErrorAlert
} from '../common'
import type { ArticleState, ArticleStateLayoutable, SelectedArticle, SelectedCell } from './types'
import type {
  fetchArticles,
  fetchLayoutableStatus,
  bulkCreateArticles,
  createArticle,
  deleteFigure,
  modifyArticle,
  modifyPage,
  modifySubLayout,
  modifyHeading,
  modifyTemplateArticle,
  createFigure,
  modifyFigure,
  modifyArea,
  deleteArticles,
  startLayout,
  switchArticleOrders,
  bulkOperateFigures
} from './articleAPI'

export const selectIdReducer = (state: Draft<ArticleState>, action: PayloadAction<number>) => {
  state.selectedIds = Array.from(new Set(state.selectedIds).add(action.payload))
}

export const unselectIdReducer = (state: Draft<ArticleState>, action: PayloadAction<number>) => {
  const set = new Set(state.selectedIds)
  set.delete(action.payload)
  state.selectedIds = Array.from(set)
}

export const selectCellReducer = (state: Draft<ArticleState>, action: PayloadAction<SelectedCell | null>) => {
  state.selectedCell = action.payload
  if (!state.selectedCell || !state.layoutable) {
    state.selectedIds = []
    return
  }
  const { articleId } = state.selectedCell
  state.selectedIds = [articleId]
}

export const setLayoutableLockVersionReducer = (state: Draft<ArticleState>, action: PayloadAction<unknown>) => {
  const lockVersion = action.payload
  if (!lockVersion || !state.layoutable) return
  state.layoutable.lockVersion = lockVersion
}

export const selectTargetArticleReducer = (
  state: Draft<ArticleState>,
  action: PayloadAction<SelectedArticle | null>
) => {
  state.selectedArticle = action.payload
  if (!state.selectedArticle || !state.layoutable) {
    state.selectedIds = []
    return
  }
  const { id: articleId } = state.selectedArticle
  state.selectedIds = [articleId]
}

export const openModalReducer = (state: Draft<ArticleState>, action: PayloadAction<string | null>) => {
  state.openedModal = action.payload
}

export const closeModalReducer = (state: Draft<ArticleState>) => {
  state.openedModal = null
}

export const buildExtraReducers = (
  builder: ActionReducerMapBuilder<ArticleState>,
  thunks: {
    fetchArticles: typeof fetchArticles
    fetchLayoutableStatus: typeof fetchLayoutableStatus
    bulkCreateArticles: typeof bulkCreateArticles
    createArticle: typeof createArticle
    deleteFigure: typeof deleteFigure
    modifyArticle: typeof modifyArticle
    modifyPage: typeof modifyPage
    modifySubLayout: typeof modifySubLayout
    modifyHeading: typeof modifyHeading
    modifyTemplateArticle: typeof modifyTemplateArticle
    createFigure: typeof createFigure
    modifyFigure: typeof modifyFigure
    modifyArea: typeof modifyArea
    deleteArticles: typeof deleteArticles
    startLayout: typeof startLayout
    switchArticleOrders: typeof switchArticleOrders
    bulkOperateFigures: typeof bulkOperateFigures
  }
) =>
  builder
    .addCase(thunks.fetchArticles.pending, DataList.fetchPending)
    .addCase(thunks.fetchArticles.fulfilled, (state, action) => {
      const { layoutableType, layoutable } = action.payload as {
        layoutableType: string
        layoutable: ArticleStateLayoutable
      }
      state.layoutableType = layoutableType
      state.layoutable = layoutable
      DataList.fetchFulfilled(state, action)
    })
    .addCase(thunks.fetchArticles.rejected, saveErrorInState(DataList.fetchRejected))
    .addCase(thunks.fetchLayoutableStatus.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.fetchLayoutableStatus.fulfilled, (state, action) => {
      state.fetching = false
      const { status } = action.payload as { status: unknown }
      if (state.layoutable) {
        state.layoutable = { ...state.layoutable, status }
      }
    })
    .addCase(thunks.fetchLayoutableStatus.rejected, (state) => {
      state.fetching = false
    })
    .addCase(thunks.createArticle.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.createArticle.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(
      thunks.createArticle.rejected,
      setErrorAlert((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.bulkCreateArticles.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.bulkCreateArticles.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(
      thunks.bulkCreateArticles.rejected,
      setErrorAlert((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.modifyArticle.pending, (state) => {
      state.fetching = true
    })
    .addCase(
      thunks.modifyArticle.fulfilled,
      setAlertState((state) => {
        state.fetching = false
      })
    )
    .addCase(
      thunks.modifyArticle.rejected,
      setErrorAlert((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.deleteArticles.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.deleteArticles.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(
      thunks.deleteArticles.rejected,
      setErrorAlert((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.switchArticleOrders.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.switchArticleOrders.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(
      thunks.switchArticleOrders.rejected,
      setErrorAlert((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.modifyPage.pending, (state) => {
      state.fetching = true
    })
    .addCase(
      thunks.modifyPage.fulfilled,
      setAlertState((state) => {
        state.fetching = false
      })
    )
    .addCase(
      thunks.modifyPage.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.modifySubLayout.pending, (state) => {
      state.fetching = true
    })
    .addCase(
      thunks.modifySubLayout.fulfilled,
      setAlertState((state) => {
        state.fetching = false
      })
    )
    .addCase(
      thunks.modifySubLayout.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.createFigure.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.createFigure.fulfilled, (state) => {
      state.fetching = false
    })
    .addCase(
      thunks.createFigure.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.modifyFigure.pending, (state) => {
      state.fetching = true
    })
    .addCase(
      thunks.modifyFigure.fulfilled,
      setAlertState((state) => {
        state.fetching = false
      })
    )
    .addCase(
      thunks.modifyFigure.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.deleteFigure.pending, (state) => {
      state.fetching = true
    })
    .addCase(
      thunks.deleteFigure.fulfilled,
      setAlertState((state) => {
        state.fetching = true
      })
    )
    .addCase(
      thunks.deleteFigure.rejected,
      saveErrorInState((state) => {
        state.fetching = true
      })
    )
    .addCase(thunks.modifyArea.pending, (state) => {
      state.fetching = true
    })
    .addCase(
      thunks.modifyArea.fulfilled,
      setAlertState((state) => {
        state.fetching = false
      })
    )
    .addCase(
      thunks.modifyArea.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.modifyHeading.pending, (state) => {
      state.fetching = true
    })
    .addCase(
      thunks.modifyHeading.fulfilled,
      setAlertState((state) => {
        state.fetching = false
      })
    )
    .addCase(
      thunks.modifyHeading.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.modifyTemplateArticle.pending, (state) => {
      state.fetching = true
    })
    .addCase(
      thunks.modifyTemplateArticle.fulfilled,
      setAlertState((state) => {
        state.fetching = false
      })
    )
    .addCase(
      thunks.modifyTemplateArticle.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.startLayout.pending, (state) => {
      state.fetching = true
    })
    .addCase(thunks.startLayout.fulfilled, (state, action) => {
      state.fetching = false
      const { status } = action.payload as { status: unknown }
      if (state.layoutable) {
        state.layoutable = { ...state.layoutable, status }
      }
      commonCloseAlert(state)
    })
    .addCase(
      thunks.startLayout.rejected,
      setErrorAlert((state) => {
        state.fetching = false
      })
    )
    .addCase(thunks.bulkOperateFigures.pending, (state) => {
      state.fetching = true
    })
    .addCase(
      thunks.bulkOperateFigures.fulfilled,
      setAlertState((state) => {
        state.fetching = false
      })
    )
    .addCase(
      thunks.bulkOperateFigures.rejected,
      saveErrorInState((state) => {
        state.fetching = false
      })
    )

export {
  commonDismissError as dismissErrorReducer,
  commonCloseAlert as closeAlertReducer,
  commonSetAlert as setAlertReducer
}
